import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileSignature, AlertCircle, ExternalLink, Send, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PendingAgreementsIndicator = ({ token }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [stats, setStats] = useState({ total: 0, unsigned_count: 0 });
  const [recentUnsigned, setRecentUnsigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadAgreementStats();
    
    // Refresh every 2 minutes
    const interval = setInterval(loadAgreementStats, 120000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadAgreementStats = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/partner-agreement/admin/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats({
          total: data.total || 0,
          unsigned_count: data.unsigned_count || 0,
          signed_count: data.signed_count || 0
        });
        // Get most recent 5 unsigned partners
        setRecentUnsigned((data.unsigned_partners || []).slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to load agreement stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async (partnerId, email, e) => {
    e.stopPropagation();
    
    try {
      const response = await fetch(`${API_URL}/api/partner-agreement/send-reminder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ partner_id: partnerId })
      });
      
      if (response.ok) {
        alert(`Erinnerung an ${email} gesendet`);
      } else {
        alert('Fehler beim Senden');
      }
    } catch (error) {
      console.error('Send reminder error:', error);
      alert('Verbindungsfehler');
    }
  };

  // Don't render if no unsigned agreements
  if (stats.unsigned_count === 0 && !loading) {
    return null;
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Agreement Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-orange-400 hover:text-orange-300 rounded-lg hover:bg-orange-500/10 transition-colors"
        title={`${stats.unsigned_count} Partner ohne Vereinbarung`}
        data-testid="pending-agreements-indicator"
      >
        <FileSignature className="w-5 h-5" />
        {stats.unsigned_count > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {stats.unsigned_count > 99 ? '99+' : stats.unsigned_count}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50">
          {/* Header */}
          <div className="bg-orange-50 px-4 py-3 border-b border-orange-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <span className="font-semibold text-gray-900">Ausstehende Vereinbarungen</span>
                  <p className="text-xs text-orange-600 font-medium">
                    {stats.unsigned_count} von {stats.total} Partnern
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Recent Unsigned Partners */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-gray-500">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-orange-500 rounded-full animate-spin mx-auto mb-2"></div>
                Lade...
              </div>
            ) : recentUnsigned.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <FileSignature className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                Alle Partner haben unterschrieben
              </div>
            ) : (
              <>
                <p className="px-4 py-2 text-xs font-medium text-gray-500 bg-gray-50">
                  Zuletzt registrierte Partner ohne Vereinbarung:
                </p>
                {recentUnsigned.map((partner) => (
                  <div
                    key={partner.id}
                    className="px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">
                          {partner.company_name || partner.email}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {partner.email}
                        </p>
                      </div>
                      <button
                        onClick={(e) => handleSendReminder(partner.id, partner.email, e)}
                        className="p-1.5 text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
                        title="Erinnerung senden"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
            <button
              onClick={() => {
                navigate('/admin/vereinbarungen');
                setIsOpen(false);
              }}
              className="w-full flex items-center justify-center gap-2 text-sm text-orange-600 hover:text-orange-700 font-medium"
              data-testid="view-all-agreements-link"
            >
              Alle Vereinbarungen verwalten
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingAgreementsIndicator;
